import { useCallback, useState } from 'react';

import { UnavailableProductOut, UnavailableProductsResponse } from '../../store/api';
import { ErrorResponse, globalErrorResponse } from '../notification/actions';
import { RemoveUnavailableProductsModalProps } from './RemoveUnavailableProductsModal';
import { useRemoveUnavailableProductsFromCartMutation } from '../../api/shopping-cart/useRemoveUnavailableProductsFromCart';

type Props = {
    readonly onFetchUnavailableProducts: () => Promise<UnavailableProductsResponse>;
};

type UseUnavailableProductsCheck = {
    readonly triggerUnavailableProductsCheck: () => Promise<void>;
    readonly getRemoveUnavailableProductsModalProps: () => RemoveUnavailableProductsModalProps;
};

type ProductData = {
    readonly withdrawnProducts: UnavailableProductOut[];
    readonly comingSoonProducts: UnavailableProductOut[];
};

const emptyProductData: ProductData = {
    withdrawnProducts: [],
    comingSoonProducts: []
};

export function useUnavailableProductsCheck({ onFetchUnavailableProducts }: Props): UseUnavailableProductsCheck {
    const [productData, setProductData] = useState<ProductData>(emptyProductData);
    const [isLoading, setIsLoading] = useState(false);
    const { isLoading: isDeleting, mutate: removeUnavailableProductsFromCart } =
        useRemoveUnavailableProductsFromCartMutation();

    const reset = useCallback(() => {
        setProductData(emptyProductData);
    }, []);

    const triggerUnavailableProductsCheck = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await onFetchUnavailableProducts();

            const comingSoonProducts = response.comingSoonProducts ?? [];
            const withdrawnProducts = response.withdrawnProducts ?? [];

            if (comingSoonProducts.length === 0 && withdrawnProducts.length === 0) {
                setProductData(emptyProductData);
            } else {
                setProductData({ withdrawnProducts, comingSoonProducts });
            }
        } catch (e) {
            globalErrorResponse(e as ErrorResponse);
            reset();
        } finally {
            setIsLoading(false);
        }
    }, [onFetchUnavailableProducts, reset]);

    const onProceed = useCallback(async () => {
        setIsLoading(true);
        try {
            return removeUnavailableProductsFromCart({
                unavailableProducts: [...productData.withdrawnProducts, ...productData.comingSoonProducts]
            });
        } finally {
            setIsLoading(false);
            reset();
        }
    }, [productData.comingSoonProducts, productData.withdrawnProducts, removeUnavailableProductsFromCart, reset]);

    return {
        triggerUnavailableProductsCheck,
        getRemoveUnavailableProductsModalProps: () => ({
            comingSoonProducts: productData.comingSoonProducts,
            withdrawnProducts: productData.withdrawnProducts,
            onProceed,
            onCancel: reset,
            isLoading: isLoading || isDeleting
        })
    };
}
